import React, { useState, useEffect } from 'react';
import { Modal, Grid, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';

import { useTranslation } from 'react-i18next';




const CreateSubtaskForm = ({ open, onClose, projectId, currentUserId, task, currentUserRoleId, onSave, errorMessage, refreshData }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [status, setStatus] = useState('to-do');
  const [assignedTo, setAssignedTo] = useState('');
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [warningMessage, setWarningMessage] = useState('');
  const { t } = useTranslation();
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  // Ana görev bilgilerini al
  const taskStartDate = task ? new Date(task.start_date) : null;
  const taskEndDate = task ? new Date(task.due_date) : null;

  useEffect(() => {
    if (task) {
      setAssignedTo(task.assigned_user_id || '');
      setTitle('');
      setDescription('');
      setDueDate('');
      setStartDate('');
      setPriority('medium');
      setStatus('to-do');
      fetchMembersWithTeams();  // Üyeleri ve takımları çek
    }
  }, [task]);

  const fetchMembersWithTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/api/projects/${projectId}/members-with-teams`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMembers(response.data);
      setFilteredMembers(response.data); // Başlangıçta tüm üyeleri ata
    } catch (error) {
    }
  };

  // Ana görevin takıma atanıp atanmadığını kontrol ederek kullanıcıları filtrele
  useEffect(() => {
    if (task?.assigned_team_id) {
      const teamMembers = members.filter(member => member.teams?.some(team => team.id === task.assigned_team_id));
      setFilteredMembers(teamMembers);
      if (!teamMembers.some(member => member.id === assignedTo)) {
        setAssignedTo(''); // Eğer seçilen kullanıcı takımda yoksa kullanıcıyı sıfırla
        setWarningMessage(t('warning_select_team_member'));
      } else {
        setWarningMessage(''); // Uyarıyı temizle
      }
    } else if (currentUserRoleId === 1) {
      setFilteredMembers(members);
      setWarningMessage('');
    }
  }, [task, members, assignedTo, currentUserRoleId]);

  const handleSave = async () => {
    // End date'in start date'ten önce olup olmadığını kontrol et
    if (new Date(dueDate) < new Date(startDate)) {
      toast.error(t('error_end_date_before_start'), { position: "top-right", autoClose: 3000 });
      return; // Eğer tarih hatalıysa işlemi durdur
    }

    const subtaskData = {
      title,
      description,
      due_date: dueDate,
      start_date: startDate,
      priority,
      status,
      assigned_to: assignedTo,
      project_id: projectId,
      task_id: task.id,
      created_by_user_id: currentUserId,
    };

    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/api/subtasks/${task.id}`, subtaskData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 201) {
        onSave(response.data);  // Yeni subtask'i ekle
        refreshData(task.id, response.data.id);
        onClose();  // Modal'ı kapat
        toast.success(t('success_subtask_created'), { position: "top-right", autoClose: 3000 });
      } else {
        toast.error(t('error_subtask_create_failed'), { position: "top-right", autoClose: 3000 });
      }
    } catch (error) {
      toast.error(t('error_subtask_create_failed'), { position: "top-right", autoClose: 3000 });
    }
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div
        style={{
          padding: 20,
          backgroundColor: 'white',
          width: '80vw',
          height: '80vh',
          margin: '5% auto',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <ToastContainer />
        <Typography variant="h6" gutterBottom>
          {t('add_new_subtask')}
        </Typography>

        {errorMessage && (
          <Typography color="error" gutterBottom>
            {errorMessage}
          </Typography>
        )}

        <TextField
          label={t('title')}
          fullWidth
          margin="normal"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          variant="outlined"
          required
        />

        <TextField
          label={t('description')}
          fullWidth
          margin="normal"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          variant="outlined"
        />

        {/* Start Date ve Due Date */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label={t('start_date')}
              fullWidth
              margin="normal"
              type="date"
              value={startDate ? formatDate(startDate) : ''}
              onChange={(e) => setStartDate(e.target.value)}
              onKeyDown={(e) => e.preventDefault()}
              InputProps={{
                inputProps: {
                  min: taskStartDate ? formatDate(taskStartDate) : '',
                  max: taskEndDate ? formatDate(taskEndDate) : '',
                },
              }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('due_date')}
              fullWidth
              margin="normal"
              type="date"
              value={dueDate ? formatDate(dueDate) : ''}
              onChange={(e) => setDueDate(e.target.value)}
              onKeyDown={(e) => e.preventDefault()}
              InputProps={{
                inputProps: {
                  min: startDate ? formatDate(new Date(startDate)) : taskStartDate ? formatDate(taskStartDate) : '',
                  max: taskEndDate ? formatDate(taskEndDate) : '',
                },
              }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              required
            />
          </Grid>
        </Grid>

        {/* Priority ve Status */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>{t('priority')}</InputLabel>
              <Select value={priority} onChange={(e) => setPriority(e.target.value)} label={t('priority')}>
                <MenuItem value="low">{t('priority_low')}</MenuItem>
                <MenuItem value="medium">{t('priority_medium')}</MenuItem>
                <MenuItem value="high">{t('priority_high')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>{t('status')}</InputLabel>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} label={t('status')}>
                <MenuItem value="to-do">{t('status_todo')}</MenuItem>
                <MenuItem value="in-progress">{t('status_inprogress')}</MenuItem>
                <MenuItem value="completed">{t('status_completed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Assigned To */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>{t('assign_to')}</InputLabel>
              <Select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                label={t('assign_to')}
                disabled={filteredMembers.length === 0}
              >
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>{t('no_available_members')}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Warning message */}
        {warningMessage && (
          <Typography color="error" style={{ marginTop: '10px' }}>
            {warningMessage}
          </Typography>
        )}

        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onClose} color="secondary">{t('cancel')}</Button>
          <Button onClick={handleSave} color="primary" disabled={!title || !startDate || !dueDate || !assignedTo}>
            {t('create_subtask')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateSubtaskForm;
